/**
 * The keybindings table, ported from t3code.
 *
 * A header row and a list of rows sharing one set of column tracks, inside a
 * horizontal scroller with a 680px floor. The floor is deliberate: four columns
 * of chords and boolean expressions have no useful narrow layout, and Forge's
 * "reflow, don't truncate" rule is answered by a real scrollbar rather than by
 * stacking a table into unreadable ribbons. The same escape hatch the CMMD
 * board takes for its status columns.
 *
 * @module
 */
import type { ReactNode } from "react";

import { ScrollArea } from "../ui/scroll-area";
import { KEYBINDING_ROW_COLUMNS } from "./KeybindingTableRow";

/** Narrower than this and the four columns stop being readable at all. */
const TABLE_MIN_WIDTH = "min-w-[680px]";

const HEADINGS = ["Command", "Keybinding", "When", "Status"] as const;

export function KeybindingsTable({
  children,
  isEmpty,
}: {
  readonly children: ReactNode;
  /** Drives the empty state, so the header stays put while a search is refined. */
  readonly isEmpty: boolean;
}) {
  return (
    <ScrollArea className="w-full max-w-full rounded-none" hideScrollbars scrollFade>
      <div
        className={`grid ${TABLE_MIN_WIDTH} ${KEYBINDING_ROW_COLUMNS} border-border/70 border-b bg-muted/25 px-4 py-2 font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.07em]`}
      >
        {HEADINGS.map((heading) => (
          <div key={heading}>{heading}</div>
        ))}
      </div>
      {/* The header is outside the list on purpose: it is not a keybinding, and
          putting it inside would make it the first `li` and flip the zebra
          striping for every row beneath it. */}
      <ul className={`${TABLE_MIN_WIDTH} divide-y divide-border/60`}>{children}</ul>
      {isEmpty ? (
        <p className="px-4 py-12 text-center text-muted-foreground text-sm" role="status">
          No keybindings match your search.
        </p>
      ) : null}
    </ScrollArea>
  );
}
